import { ensureItemFonts, getCachedFont, type FontkitFont } from './fontEngine';
import { normalizeBadge, normalizeTypography, TYPOGRAPHY_BADGE_FRAME } from './config';
import type { BadgeTextRegion, TypographyAppearance, TypographyBadgeConfig, TypographyBounds, TypographyConfig, TypographyDesignItem, TypographyFrame, TypographyRenderMetrics } from './types';
import { arcPath, linePath, ringPath, wavePath, type CurvePath, type PathPoint } from '@shared/curveText/pathGeometry';
import {
  balancedRunCenter,
  capHeight,
  escapeXml,
  fitRunSize,
  glyphMarkup,
  placeOnPath,
  placeRun,
  runCenterOf,
  shapeRun,
  transformedGlyphBounds,
  unitsPerEm,
  type GlyphFrame,
  type PlacedGlyph,
  type ShapedGlyph,
} from '@shared/curveText/placeRun';

type MutableBounds = { minX: number; minY: number; maxX: number; maxY: number };

function emptyBounds(): MutableBounds {
  return { minX: Number.POSITIVE_INFINITY, minY: Number.POSITIVE_INFINITY, maxX: Number.NEGATIVE_INFINITY, maxY: Number.NEGATIVE_INFINITY };
}

function addBounds(target: MutableBounds, box: { minX: number; minY: number; maxX: number; maxY: number }, offsetX = 0, offsetY = 0): void {
  target.minX = Math.min(target.minX, box.minX + offsetX);
  target.minY = Math.min(target.minY, box.minY + offsetY);
  target.maxX = Math.max(target.maxX, box.maxX + offsetX);
  target.maxY = Math.max(target.maxY, box.maxY + offsetY);
}

function expandBounds(target: MutableBounds, amount: number): void {
  if (!Number.isFinite(target.minX) || amount <= 0) return;
  target.minX -= amount;
  target.minY -= amount;
  target.maxX += amount;
  target.maxY += amount;
}

function finishBounds(box: MutableBounds): TypographyBounds {
  if (![box.minX, box.minY, box.maxX, box.maxY].every(Number.isFinite)) return { x: 0, y: 0, width: 1, height: 1 };
  const width = Math.max(1, box.maxX - box.minX);
  const height = Math.max(1, box.maxY - box.minY);
  return { x: box.minX, y: box.minY, width, height };
}

function frameFor(item: TypographyDesignItem): TypographyFrame {
  // Badge presets are authored against one stable coordinate system. Legacy
  // designer items stored their display box as typographyFrame, which changed
  // glyph and separator geometry when the print area was small.
  if (item.typographyBadge) return { ...TYPOGRAPHY_BADGE_FRAME };
  const candidate = item.typographyFrame && typeof item.typographyFrame === 'object' ? item.typographyFrame as Record<string, unknown> : {};
  return {
    width: Math.max(24, numberValue(candidate.width, numberValue(item.width, 300))),
    height: Math.max(24, numberValue(candidate.height, numberValue(item.height, 160))),
  };
}

function scaleFor(item: TypographyDesignItem): number {
  return Math.max(0.01, numberValue(item.typographyScale, 1));
}

function numberValue(value: unknown, fallback: number): number {
  const candidate = Number(value);
  return Number.isFinite(candidate) ? candidate : fallback;
}

function sanitizeId(value: string): string {
  return value.replace(/[^a-z0-9_-]/gi, '-');
}

function autoFitSize(font: FontkitFont, text: string, requested: number, spacing: number, available: number, config: TypographyConfig): number {
  if (!config.layout.autoFit.enabled) return requested;
  const min = config.layout.autoFit.minSize;
  const max = Math.min(config.layout.autoFit.maxSize, requested);
  return Math.max(min, Math.min(requested, fitRunSize(font, text, spacing, available, min, max)));
}

/**
 * Original (curveVersion 1) placement, kept verbatim so saved designs and placed orders render
 * exactly as the shopper approved them: wave and rise step along x, and the ring has a fixed
 * radius that stretches or squeezes letter gaps to fill its sweep.
 */
function placeGlyphsV1(shaped: ShapedGlyph[], total: number, width: number, height: number, fontSize: number, config: TypographyConfig): PlacedGlyph[] {
  const centerX = width / 2;
  const centerY = height / 2;
  const mode = config.layout.mode;
  const phase = config.layout.phase * Math.PI / 180;
  const arcAngle = Math.max(0.001, Math.abs(config.layout.arcAngle) * Math.PI / 180);
  const arcDirection = (config.layout.arcAngle >= 0 ? 1 : -1) * (config.layout.reverse ? -1 : 1) as 1 | -1;
  const arc = arcPath(centerX, centerY, total / arcAngle, arcDirection);
  const circleSweep = config.layout.circleSweep * Math.PI / 180;
  const circleDirection = config.layout.reverse ? -1 : 1;
  const circleRadius = Math.min(width, height) * 0.38;
  const circlePoint: CurvePath = (offset) => {
    const theta = (total > 0 ? offset / total : 0) * circleSweep * circleDirection;
    return {
      x: centerX + circleRadius * Math.sin(theta),
      y: centerY - circleRadius * Math.cos(theta),
      rotation: theta * 180 / Math.PI + (config.layout.side === 'inside' ? 180 : 0),
    };
  };
  let runCenter = runCenterOf(shaped, total);
  if (mode === 'arc') runCenter = balancedRunCenter(shaped, total, arc, arcDirection);
  else if (mode === 'circle') runCenter = balancedRunCenter(shaped, total, circlePoint, 1);

  return placeRun(shaped, (middle): GlyphFrame => {
    const offset = middle - runCenter;
    const normalized = total > 0 ? offset / total : 0;
    if (mode === 'arc') return arc(offset);
    if (mode === 'circle') return circlePoint(offset);
    if (mode === 'wave' || mode === 'flag') {
      const cycles = config.layout.frequency * Math.PI * 2;
      const amplitude = config.layout.amplitude * 0.5;
      const wave = normalized * cycles + phase;
      const rotation = mode === 'wave' ? Math.atan(Math.cos(wave) * amplitude * cycles / Math.max(1, total)) * 180 / Math.PI : 0;
      return { x: centerX + offset, y: centerY + Math.sin(wave) * amplitude, rotation };
    }
    if (mode === 'rise') {
      return { x: centerX + offset, y: centerY - normalized * total * Math.tan(config.layout.slope * Math.PI / 180), rotation: -config.layout.slope };
    }
    if (mode === 'bulge') {
      const envelope = Math.max(0, 1 - 4 * normalized * normalized);
      return { x: centerX + offset, y: centerY - envelope * config.layout.bulge * 0.45, rotation: 0, scaleY: Math.max(0.2, 1 + envelope * config.layout.bulge / 100) };
    }
    return { x: centerX + offset, y: centerY + fontSize * 0.32, rotation: 0 };
  });
}

/**
 * Current placement: every curved mode walks its path by arc length, so a glyph's advance is
 * exactly the distance to its neighbour along the baseline, whatever the curvature.
 */
function placeGlyphsV2(font: FontkitFont, shaped: ShapedGlyph[], total: number, width: number, height: number, fontSize: number, config: TypographyConfig): PlacedGlyph[] {
  const centerX = width / 2;
  const centerY = height / 2;
  const layout = config.layout;
  const mode = layout.mode;

  if (mode === 'arc') {
    // A positive arcAngle arches up, matching the legacy text-arc convention
    // (`ArcLayout.measureArcTextLayout`) and the `textCurve = arcAngle / 3.6` mirror the panel
    // writes for it. Presets named "Classic Arch" / "Full Rainbow" read as arches because of it.
    const arcAngle = Math.max(0.001, Math.abs(layout.arcAngle) * Math.PI / 180);
    const direction = (layout.arcAngle >= 0 ? 1 : -1) * (layout.reverse ? -1 : 1) as 1 | -1;
    const path = arcPath(centerX, centerY, total / arcAngle, direction);
    return placeOnPath(shaped, path, balancedRunCenter(shaped, total, path, direction));
  }
  if (mode === 'circle') {
    // The ring is as big as the text needs to cover the sweep at its natural spacing (Canva's
    // model); `reverse` anchors the run at the bottom so it still reads left-to-right, upright.
    const sweep = Math.max(0.01, layout.circleSweep * Math.PI / 180);
    const radius = Math.max(1, total / sweep);
    const bottom = layout.reverse;
    const path = ringPath(centerX, centerY, radius, radius, bottom);
    const shift = ringShift(font, fontSize, bottom, layout.side);
    return placeOnPath(shaped, path, balancedRunCenter(shaped, total, path, bottom ? -1 : 1, shift), shift);
  }
  if (mode === 'wave') {
    const path = wavePath(centerX, centerY, layout.amplitude * 0.5, layout.frequency, layout.phase * Math.PI / 180, total);
    return placeOnPath(shaped, path, runCenterOf(shaped, total));
  }
  if (mode === 'rise') {
    return placeOnPath(shaped, linePath(centerX, centerY, layout.slope), runCenterOf(shaped, total));
  }

  // Flag and bulge keep glyphs upright, so stepping along x is the natural spacing for them.
  const runCenter = runCenterOf(shaped, total);
  const phase = layout.phase * Math.PI / 180;
  return placeRun(shaped, (middle): GlyphFrame => {
    const offset = middle - runCenter;
    const normalized = total > 0 ? offset / total : 0;
    if (mode === 'flag') {
      return { x: centerX + offset, y: centerY + Math.sin(normalized * layout.frequency * Math.PI * 2 + phase) * layout.amplitude * 0.5, rotation: 0 };
    }
    if (mode === 'bulge') {
      const envelope = Math.max(0, 1 - 4 * normalized * normalized);
      return { x: centerX + offset, y: centerY - envelope * layout.bulge * 0.45, rotation: 0, scaleY: Math.max(0.2, 1 + envelope * layout.bulge / 100) };
    }
    return { x: centerX + offset, y: centerY + fontSize * 0.32, rotation: 0 };
  });
}

/**
 * Baseline shift that puts a ring run's ink on the requested side while it keeps reading upright.
 * From the top anchor glyphs stand on the ring (outside) or hang below it by a cap height
 * (inside); from the bottom anchor it is the other way round.
 */
function ringShift(font: FontkitFont, fontSize: number, bottom: boolean, side: 'outside' | 'inside'): number {
  return (side === 'inside') !== bottom ? capHeight(font, fontSize) : 0;
}

function placeGlyphs(font: FontkitFont, shaped: ShapedGlyph[], total: number, width: number, height: number, fontSize: number, config: TypographyConfig): PlacedGlyph[] {
  return config.layout.curveVersion === 2
    ? placeGlyphsV2(font, shaped, total, width, height, fontSize, config)
    : placeGlyphsV1(shaped, total, width, height, fontSize, config);
}

const fontIds = new WeakMap<object, number>();
let nextFontId = 1;
function fontId(font: FontkitFont | null): number {
  if (!font) return 0;
  let id = fontIds.get(font);
  if (!id) {
    id = nextFontId++;
    fontIds.set(font, id);
  }
  return id;
}

const METRICS_CACHE_LIMIT = 48;
const metricsCache = new Map<string, TypographyRenderMetrics | null>();

/**
 * Metrics are rebuilt by the renderer, the bounds resolver, preflight and every panel patch for
 * the same item state; one slider tick used to lay the run out four or five times.
 */
function memoMetrics(item: TypographyDesignItem, fonts: Array<FontkitFont | null>, build: () => TypographyRenderMetrics | null): TypographyRenderMetrics | null {
  const key = JSON.stringify([
    item.id, item.text, item.fontSize, item.width, item.height, item.opacity, item.color, item.typographyScale,
    item.typographyFrame, item.typography, item.typographyBadge, fonts.map(fontId),
  ]);
  if (metricsCache.has(key)) {
    const hit = metricsCache.get(key) ?? null;
    metricsCache.delete(key);
    metricsCache.set(key, hit);
    return hit;
  }
  const metrics = build();
  metricsCache.set(key, metrics);
  if (metricsCache.size > METRICS_CACHE_LIMIT) metricsCache.delete(metricsCache.keys().next().value as string);
  return metrics;
}

function visualBounds(glyphs: PlacedGlyph[], appearance: TypographyAppearance): MutableBounds {
  const base = emptyBounds();
  for (const glyph of glyphs) if (glyph.d) addBounds(base, transformedGlyphBounds(glyph));
  const result = { ...base };
  if (appearance.stroke.enabled) expandBounds(result, appearance.stroke.width / 2);
  if (appearance.shadow.enabled) {
    for (const layer of appearance.shadow.layers) {
      const layerBox = { ...base };
      expandBounds(layerBox, layer.spread + (layer.blur > 0 ? layer.blur + 2 : 0));
      addBounds(result, layerBox, layer.x, layer.y);
    }
  }
  if (appearance.extrude.enabled && appearance.extrude.depth > 0) {
    const angle = appearance.extrude.angle * Math.PI / 180;
    addBounds(result, base, Math.cos(angle) * appearance.extrude.depth, Math.sin(angle) * appearance.extrude.depth);
  }
  return result;
}

// Older librsvg builds (used by the rsvg-convert export path) mis-parse 8-digit
// hex, so alpha is split into a separate opacity value.
function paint(hex: string): { color: string; opacity: number } {
  const value = hex.trim();
  const long = /^#([0-9a-f]{6})([0-9a-f]{2})$/i.exec(value);
  if (long) return { color: `#${long[1]}`, opacity: Math.round(parseInt(long[2], 16) / 255 * 1000) / 1000 };
  const short = /^#([0-9a-f]{3})([0-9a-f])$/i.exec(value);
  if (short) return { color: `#${short[1]}`, opacity: Math.round(parseInt(short[2] + short[2], 16) / 255 * 1000) / 1000 };
  return { color: value, opacity: 1 };
}

function gradientStops(colors: string[]): string {
  return colors.map((entry, index, list) => {
    const stop = paint(entry);
    return `<stop offset="${list.length === 1 ? 0 : index / (list.length - 1) * 100}%" stop-color="${escapeXml(stop.color)}"${stop.opacity < 1 ? ` stop-opacity="${stop.opacity}"` : ''}/>`;
  }).join('');
}

function appearanceMarkup(glyphs: PlacedGlyph[], appearance: TypographyAppearance, id: string, opacity = 1): { defs: string; body: string } {
  const safeId = sanitizeId(id);
  const gradientId = `typography-gradient-${safeId}`;
  // Glyph paths live in font units and are scaled down by their transform, so
  // stroke widths (in user units) must be scaled up to survive that transform.
  const glyphScale = glyphs.find((glyph) => glyph.d)?.scaleX || 1;
  const strokeUnits = (width: number) => Number((width / glyphScale).toFixed(2));
  let defs = '';
  if (appearance.fill.type === 'linear-gradient') {
    const radians = appearance.fill.angle * Math.PI / 180;
    const x1 = 50 - Math.cos(radians) * 50;
    const y1 = 50 - Math.sin(radians) * 50;
    const x2 = 50 + Math.cos(radians) * 50;
    const y2 = 50 + Math.sin(radians) * 50;
    defs += `<linearGradient id="${gradientId}" x1="${x1}%" y1="${y1}%" x2="${x2}%" y2="${y2}%">${gradientStops(appearance.fill.colors)}</linearGradient>`;
  } else if (appearance.fill.type === 'radial-gradient') {
    defs += `<radialGradient id="${gradientId}" cx="50%" cy="50%" r="65%">${gradientStops(appearance.fill.colors)}</radialGradient>`;
  }
  const chunks: string[] = [];
  if (appearance.extrude.enabled) {
    const angle = appearance.extrude.angle * Math.PI / 180;
    const depthPaint = paint(appearance.extrude.color);
    const depthOpacity = depthPaint.opacity < 1 ? ` opacity="${depthPaint.opacity}"` : '';
    for (let step = appearance.extrude.steps; step >= 1; step -= 1) {
      const distance = appearance.extrude.depth * step / appearance.extrude.steps;
      chunks.push(`<g fill="${escapeXml(depthPaint.color)}"${depthOpacity}>${glyphMarkup(glyphs, Math.cos(angle) * distance, Math.sin(angle) * distance)}</g>`);
    }
  }
  if (appearance.shadow.enabled) {
    const hollow = appearance.fill.type === 'none';
    // layers[0] paints on top of later layers, matching CSS text-shadow order.
    for (let index = appearance.shadow.layers.length - 1; index >= 0; index -= 1) {
      const layer = appearance.shadow.layers[index];
      const layerPaint = paint(layer.color);
      let filter = '';
      if (layer.blur > 0) {
        const filterId = `typography-shadow-${safeId}-${index}`;
        // Oversized filter region so librsvg does not clip the halo at the
        // default -10%..120% box; stdDeviation = blur/2 tracks CSS blur radius.
        defs += `<filter id="${filterId}" x="-100%" y="-100%" width="300%" height="300%"><feGaussianBlur stdDeviation="${layer.blur / 2}"/></filter>`;
        filter = ` filter="url(#${filterId})"`;
      }
      // Hollow text glows as an outline ring, not as a filled silhouette.
      const strokeWidth = layer.spread * 2 + (hollow ? appearance.stroke.width : 0);
      const stroke = strokeWidth > 0 ? ` stroke="${escapeXml(layerPaint.color)}" stroke-width="${strokeUnits(strokeWidth)}" stroke-linejoin="round"` : '';
      const layerOpacity = layerPaint.opacity < 1 ? ` opacity="${layerPaint.opacity}"` : '';
      chunks.push(`<g fill="${hollow ? 'none' : escapeXml(layerPaint.color)}"${layerOpacity}${stroke}${filter}>${glyphMarkup(glyphs, layer.x, layer.y)}</g>`);
    }
  }
  const fillPaint = paint(appearance.fill.color);
  const fill = appearance.fill.type === 'linear-gradient' || appearance.fill.type === 'radial-gradient'
    ? `url(#${gradientId})`
    : appearance.fill.type === 'none' ? 'none' : fillPaint.color;
  const fillOpacity = appearance.fill.type === 'solid' ? opacity * fillPaint.opacity : opacity;
  const stroke = appearance.stroke.enabled ? ` stroke="${escapeXml(appearance.stroke.color)}" stroke-width="${strokeUnits(appearance.stroke.width)}" stroke-linejoin="round" paint-order="stroke fill"` : '';
  chunks.push(`<g fill="${escapeXml(fill)}" opacity="${fillOpacity}"${stroke}>${glyphMarkup(glyphs)}</g>`);
  return { defs, body: chunks.join('') };
}

export function typographyFontReady(item: TypographyDesignItem): boolean {
  return Boolean(getCachedFont(item));
}

export function badgeFontsReady(item: TypographyDesignItem): boolean {
  const badge = normalizeBadge(item.typographyBadge);
  const regionItem = (region: BadgeTextRegion) => ({ ...item, fontFamily: region.fontFamily, fontWeight: region.fontWeight });
  return [badge.top, badge.bottom, ...(badge.center.enabled ? [badge.center] : [])].every((region) => Boolean(getCachedFont(regionItem(region))));
}

function svgDocument(item: TypographyDesignItem, bounds: TypographyBounds, defs: string, body: string): TypographyRenderMetrics {
  const scale = scaleFor(item);
  const width = Math.max(1, bounds.width * scale);
  const height = Math.max(1, bounds.height * scale);
  const outputWidth = Math.max(1, numberValue(item.width, width));
  const outputHeight = Math.max(1, numberValue(item.height, height));
  const viewBox = [bounds.x, bounds.y, bounds.width, bounds.height].map((value) => Number(value.toFixed(3))).join(' ');
  return {
    bounds,
    width,
    height,
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" width="${outputWidth}" height="${outputHeight}"><defs>${defs}</defs>${body}</svg>`,
  };
}

export function buildTypographyMetrics(item: TypographyDesignItem): TypographyRenderMetrics | null {
  const font = getCachedFont(item);
  if (!font) return null;
  return memoMetrics(item, [font], () => {
    const config = normalizeTypography(item.typography, String(item.color ?? '#111827'));
    const maxChars = config.layout.autoFit.maxChars;
    const text = Array.from(String(item.text ?? '').normalize('NFC')).slice(0, maxChars).join('');
    if (!text) return null;
    const frame = frameFor(item);
    const requestedSize = numberValue(item.fontSize, 40);
    const fontSize = autoFitSize(font, text, requestedSize, config.layout.spacing, frame.width * 0.9, config);
    const shaped = shapeRun(font, text, fontSize, config.layout.spacing);
    const glyphs = placeGlyphs(font, shaped.glyphs, shaped.total, frame.width, frame.height, fontSize, config);
    const appearance = appearanceMarkup(glyphs, config.appearance, item.id, numberValue(item.opacity, 1));
    return svgDocument(item, finishBounds(visualBounds(glyphs, config.appearance)), appearance.defs, appearance.body);
  });
}

export function buildTypographySvg(item: TypographyDesignItem): string {
  return buildTypographyMetrics(item)?.svg ?? '';
}

function fitBadgeSize(font: FontkitFont, region: BadgeTextRegion, available: number, fit: TypographyBadgeConfig['geometry']['autoFit']): number {
  if (!fit.enabled) return region.fontSize;
  const low = Math.min(fit.minSize, fit.maxSize);
  const size = fitRunSize(font, region.text, region.spacing, available, low, Math.max(low, fit.maxSize));
  return Math.max(fit.minSize, Math.min(fit.maxSize, size));
}

function circularRegion(font: FontkitFont, region: BadgeTextRegion, width: number, height: number, sweepDegrees: number, bottom: boolean, ellipse: boolean, side: 'outside'|'inside', padding: number, fit: TypographyBadgeConfig['geometry']['autoFit'], curveVersion: 1 | 2): PlacedGlyph[] {
  const sweep = sweepDegrees * Math.PI / 180;
  const radiusX = Math.max(1, width / 2 - padding);
  const radiusY = Math.max(1, ellipse ? height / 2 - padding * 1.6 : Math.min(width, height) / 2 - padding);
  const fontSize = fitBadgeSize(font, region, Math.max(1, (radiusX + radiusY) / 2 * sweep * 0.92), fit);
  const shaped = shapeRun(font, region.text, fontSize, region.spacing);
  if (curveVersion === 2) {
    // Arc-length along the ring, true tangent, and always upright at its anchor: the bottom line
    // reads left-to-right instead of upside down, and `side` only moves the ink across the ring.
    const path = ringPath(width / 2, height / 2, radiusX, radiusY, bottom);
    const shift = ringShift(font, fontSize, bottom, side);
    return placeOnPath(shaped.glyphs, path, balancedRunCenter(shaped.glyphs, shaped.total, path, bottom ? -1 : 1, shift), shift);
  }
  const regionPoint: CurvePath = (offset: number): PathPoint => {
    const theta = (shaped.total ? offset / shaped.total : 0) * sweep;
    return {
      x: width / 2 + radiusX * Math.sin(theta),
      y: height / 2 + (bottom ? radiusY : -radiusY) * Math.cos(theta),
      rotation: (bottom ? -theta * 180 / Math.PI + 180 : theta * 180 / Math.PI) + (side === 'inside' ? 180 : 0),
    };
  };
  return placeOnPath(shaped.glyphs, regionPoint, balancedRunCenter(shaped.glyphs, shaped.total, regionPoint, bottom ? -1 : 1));
}

function straightRegion(font: FontkitFont, region: BadgeTextRegion, width: number, height: number, padding: number, fit: TypographyBadgeConfig['geometry']['autoFit']): PlacedGlyph[] {
  const fontSize = fitBadgeSize(font, region, Math.max(1, width - padding * 2), fit);
  const shaped = shapeRun(font, region.text, fontSize, region.spacing);
  // Straight runs start at the glyph's own origin, not centred on its slot.
  const scale = fontSize / unitsPerEm(font);
  let cursor = width / 2 - shaped.total / 2;
  let pen = cursor;
  return shaped.glyphs.map((glyph) => {
    const x = glyph.mark ? pen + glyph.offsetX : cursor + glyph.offsetX;
    const placed = { d: glyph.d, path: glyph.path, x, y: height / 2 - glyph.offsetY + fontSize * 0.32, rotation: 0, scaleX: scale, scaleY: scale };
    if (!glyph.mark) {
      pen = cursor + glyph.outline;
      cursor += glyph.advance;
    }
    return placed;
  });
}

function addSeparatorBounds(target: MutableBounds, config: TypographyBadgeConfig, width: number, height: number): void {
  if (config.separator.kind === 'none') return;
  const radius = config.separator.kind === 'dot' ? config.separator.size / 2 : config.separator.size;
  for (const x of [width * 0.16, width * 0.84]) {
    addBounds(target, { minX: x - radius, minY: height / 2 - radius, maxX: x + radius, maxY: height / 2 + radius });
  }
}

function separatorMarkup(config: TypographyBadgeConfig, width: number, height: number): string {
  if (config.separator.kind === 'none') return '';
  const size = config.separator.size;
  const build = (x: number) => {
    if (config.separator.kind === 'dot') return `<circle cx="${x}" cy="${height / 2}" r="${size / 2}"/>`;
    if (config.separator.kind === 'diamond') return `<path d="M ${x} ${height / 2 - size} L ${x + size} ${height / 2} L ${x} ${height / 2 + size} L ${x - size} ${height / 2} Z"/>`;
    const points = Array.from({ length: 10 }, (_, index) => {
      const radius = index % 2 === 0 ? size : size * 0.42;
      const angle = -Math.PI / 2 + index * Math.PI / 5;
      return `${x + Math.cos(angle) * radius},${height / 2 + Math.sin(angle) * radius}`;
    }).join(' ');
    return `<polygon points="${points}"/>`;
  };
  return `<g fill="${escapeXml(config.separator.color)}">${build(width * 0.16)}${build(width * 0.84)}</g>`;
}

export function buildBadgeMetrics(item: TypographyDesignItem): TypographyRenderMetrics | null {
  const badge = normalizeBadge(item.typographyBadge);
  const frame = frameFor(item);
  const width = frame.width;
  const height = frame.height;
  const fontFor = (region: BadgeTextRegion) => getCachedFont({ ...item, fontFamily: region.fontFamily, fontWeight: region.fontWeight });
  const topFont = fontFor(badge.top);
  const bottomFont = fontFor(badge.bottom);
  const centerFont = badge.center.enabled ? fontFor(badge.center) : null;
  if (!topFont || !bottomFont || (badge.center.enabled && !centerFont)) return null;
  return memoMetrics(item, [topFont, bottomFont, centerFont], () => buildBadgeBody(item, badge, width, height, topFont, bottomFont, centerFont));
}

function buildBadgeBody(item: TypographyDesignItem, badge: TypographyBadgeConfig, width: number, height: number, topFont: FontkitFont, bottomFont: FontkitFont, centerFont: FontkitFont | null): TypographyRenderMetrics {
  const ellipse = badge.geometry.shape === 'ellipse';
  const topGlyphs = circularRegion(topFont, badge.top, width, height, badge.geometry.topSweep, false, ellipse, badge.geometry.topSide, badge.geometry.padding, badge.geometry.autoFit, badge.geometry.curveVersion);
  const bottomGlyphs = circularRegion(bottomFont, badge.bottom, width, height, badge.geometry.bottomSweep, true, ellipse, badge.geometry.bottomSide, badge.geometry.padding, badge.geometry.autoFit, badge.geometry.curveVersion);
  const centerGlyphs = badge.center.enabled && centerFont ? straightRegion(centerFont, badge.center, width, height, badge.geometry.padding, badge.geometry.autoFit) : [];
  const opacity = numberValue(item.opacity, 1);
  const regionAppearance = (glyphs: PlacedGlyph[], region: BadgeTextRegion, key: string) => appearanceMarkup(
    glyphs,
    { ...badge.appearance, fill: { ...badge.appearance.fill, color: region.color } },
    `${item.id}-${key}`,
    opacity,
  );
  const topAppearance = regionAppearance(topGlyphs, badge.top, 'top');
  const bottomAppearance = regionAppearance(bottomGlyphs, badge.bottom, 'bottom');
  const centerAppearance = regionAppearance(centerGlyphs, badge.center, 'center');
  const bounds = emptyBounds();
  addBounds(bounds, visualBounds(topGlyphs, badge.appearance));
  addBounds(bounds, visualBounds(bottomGlyphs, badge.appearance));
  if (centerGlyphs.length) addBounds(bounds, visualBounds(centerGlyphs, badge.appearance));
  addSeparatorBounds(bounds, badge, width, height);
  return svgDocument(
    item,
    finishBounds(bounds),
    `${topAppearance.defs}${bottomAppearance.defs}${centerAppearance.defs}`,
    `${topAppearance.body}${bottomAppearance.body}${centerAppearance.body}${separatorMarkup(badge, width, height)}`,
  );
}

export function buildBadgeSvg(item: TypographyDesignItem): string {
  return buildBadgeMetrics(item)?.svg ?? '';
}

export function buildItemSvg(item: TypographyDesignItem): string {
  return item.typographyBadge ? buildBadgeSvg(item) : buildTypographySvg(item);
}

export function measureItem(item: TypographyDesignItem): { width: number; height: number } {
  const metrics = item.typographyBadge ? buildBadgeMetrics(item) : buildTypographyMetrics(item);
  if (!metrics) return { width: Math.max(1, numberValue(item.width, item.typographyBadge ? 320 : 300)), height: Math.max(1, numberValue(item.height, item.typographyBadge ? 320 : 160)) };
  return { width: Math.max(24, Math.ceil(metrics.width)), height: Math.max(24, Math.ceil(metrics.height)) };
}

/** Build one atomic, center-preserving patch after a Typography Pro property edit. */
export function fitItemPatch(item: TypographyDesignItem, patch: Record<string, unknown>): Record<string, unknown> {
  const frame = frameFor(item);
  const current = { ...item, typographyFrame: frame };
  const previousMetrics = item.typographyBadge ? buildBadgeMetrics(current) : buildTypographyMetrics(current);
  const next = { ...current, ...patch, typographyFrame: frame };
  const nextMetrics = next.typographyBadge ? buildBadgeMetrics(next) : buildTypographyMetrics(next);
  if (!previousMetrics || !nextMetrics) return { ...patch, typographyFrame: frame };

  const currentWidth = Math.max(1, numberValue(item.width, previousMetrics.bounds.width));
  const currentHeight = Math.max(1, numberValue(item.height, previousMetrics.bounds.height));
  const scale = Math.max(0.01, Math.min(currentWidth / previousMetrics.bounds.width, currentHeight / previousMetrics.bounds.height));
  const width = Math.max(24, Math.ceil(nextMetrics.bounds.width * scale));
  const height = Math.max(24, Math.ceil(nextMetrics.bounds.height * scale));
  const centerX = numberValue(item.left, 0) + currentWidth / 2;
  const centerY = numberValue(item.top, 0) + currentHeight / 2;
  return {
    ...patch,
    typographyFrame: frame,
    typographyScale: scale,
    width,
    height,
    left: centerX - width / 2,
    top: centerY - height / 2,
  };
}

/**
 * Record a box the shopper dragged the layer to, as a pure visual scale.
 *
 * The glyph layout is authored against `typographyFrame` and must not re-flow on a resize, so
 * neither the frame nor `fontSize` moves here — only `typographyScale`, the same ratio
 * `fitItemPatch` derives, so `measureItem` agrees with the new box and later panel edits keep it.
 */
export function fitBoxPatch(item: TypographyDesignItem, box: { width: number; height: number }): Record<string, unknown> | null {
  const metrics = item.typographyBadge ? buildBadgeMetrics(item) : buildTypographyMetrics(item);
  if (!metrics || metrics.bounds.width <= 0 || metrics.bounds.height <= 0) return null;
  const width = Math.max(1, numberValue(box.width, 1));
  const height = Math.max(1, numberValue(box.height, 1));
  const scale = Math.max(0.01, Math.min(width / metrics.bounds.width, height / metrics.bounds.height));
  return { typographyScale: scale };
}

export function renderItem(item: TypographyDesignItem, container: HTMLElement): () => void {
  const host = document.createElement('div');
  host.style.width = '100%';
  host.style.height = '100%';
  host.style.pointerEvents = 'auto';
  const paint = (svg: string) => {
    host.className = '';
    host.textContent = '';
    host.innerHTML = svg;
    const node = host.firstElementChild as SVGElement | null;
    if (node) {
      node.setAttribute('width', '100%');
      node.setAttribute('height', '100%');
      node.style.overflow = 'visible';
    }
  };
  container.appendChild(host);
  const svg = buildItemSvg(item);
  if (svg) {
    paint(svg);
    return () => host.remove();
  }
  // The face is still loading (a style change asks for a new file). Keep the box quiet instead
  // of flashing an error: the font engine falls back to the closest face the family ships, so
  // the only way to end up without outlines is a family that cannot be loaded at all.
  let active = true;
  void ensureItemFonts(item).then((ready) => {
    if (!active) return;
    const next = ready ? buildItemSvg(item) : '';
    if (next) {
      paint(next);
      return;
    }
    host.className = 'vptw:w-full vptw:h-full vptw:flex vptw:items-center vptw:justify-center vptw:text-center vptw:text-xs vptw:text-amber-700 vptw:bg-amber-50/80';
    host.textContent = 'Typography font unavailable';
  });
  return () => { active = false; host.remove(); };
}
